import React, { ComponentProps, useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import * as withContainer from '../../graphql/containers';
import { Bank, PurchaseOrder, PurchaseOrderCancelReceipt } from '../../graphql/entities';
import mapStateToFormProps from '../../utils/mapStateToFormProps';
import createForm from '../../components/connectedForm/ConnectedForm';
import { default as Form } from '../../components/connectedForm';
import * as input from '../../components/connectedForm/inputComponents';
import Formatters from '../../components/grid-addons/formatters';
import GoodsReceiptHelperQuery from 'graphql/queries/product/GoodsReceiptHelperQuery.graphql';
import productDropdown from '../../utils/GridDropdownSetup';
import PurchaseOrderHeader from '../../components/PurchaseOrderHeader/PurchaseOrderHeaderComponent';
import OrderTotals, { OrderTotalsOriginModule } from '../../components/CommonOrderComponents/OrderTotals';
import {
  getQuantityPrecision,
  useGetCostPricePrecision,
} from '../../components/CommonOrderComponents/CommonOrderFunctions';
import { ICommit, IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import {
  AttributeType,
  IGoodsReceiptHelperQueryResult,
  IGoodsReceiptHelperQueryVariables,
  ILotAttributeConfig,
  IPurchaseOrderLine,
  IPurchaseOrderLineInput,
  IPurchaseOrderReceiptAdjustment,
  IPurchaseOrderReceiptAdjustmentInput,
  Maybe,
} from '../../graphql/schema/p4types';
import BlurTextField from '../../components/connectedForm/BlurTextField';
import { AddressInfo } from '../../components/CommonOrderComponents/AddressInfo';
import { IEditableDataGridProps, IOnCellSelectedFunction } from '../../components/EditableDataGrid/EditableDataGrid';
import { IDateFieldProps } from '../../components/connectedForm/DateInput/DateInput';
import { cloneDeep } from '@apollo/client/utilities';
import { useFormQuery } from '../../graphql/containers/useFormQuery';

const { instance, update, type, formTitle } = PurchaseOrderCancelReceipt;

const PURCHASE_ORDER_CANCELLATION_SAVE_OPTIONS = [
  {
    value: 'EXIT',
    label: `Save Cancellations`,
  },
];

const useOrderLineColumnMeta = () => {
  const getCostPricePrecision = useGetCostPricePrecision();

  return [
    {
      name: 'Product',
      key: 'product',
      editable: false,
      options: productDropdown(),
      formatter: Formatters.ReferenceWithSupplierPartNumberPillFormatter,
      width: 300,
      resizable: false,
    },
    {
      name: 'Description',
      key: 'product.description',
      editable: false,
      resizable: true,
    },
    {
      name: 'Warehouse',
      key: 'warehouse.description',
      editable: false,
      resizable: true,
      width: 200,
    },
    {
      name: 'Price',
      key: 'nettSellingPrice',
      editable: true,
      formatter: Formatters.NumericFormatter,
      resizable: true,
      cellClass: 'rightAlign',
      options: {
        format: { decimals: getCostPricePrecision },
      },
      width: 200,
    },
    {
      name: 'Ordered',
      key: 'quantity',
      formatter: Formatters.NumericFormatter,
      editable: false,
      resizable: true,
      width: 200,
      cellClass: 'rightAlign',
      options: {
        format: { decimals: getQuantityPrecision },
      },
    },
    {
      name: 'Received',
      key: 'received',
      formatter: Formatters.NumericFormatter,
      editable: false,
      resizable: true,
      cellClass: 'rightAlign',
      options: {
        format: { decimals: getQuantityPrecision },
      },
      width: 200,
    },
  ];
};

type IPurchaseOrderCancelReceiptFormProps = { values: IPurchaseOrderReceiptAdjustment };

const PurchaseOrderCancelReceiptForm = (props: IPurchaseOrderCancelReceiptFormProps) => {
  const [rowIdx, setRowIdx] = useState<number>(-1); //Initialized to -1 because we load the secondary grid when it 'changes' (So '0' is not a valid starting point).
  const [orderLineReceiptColumns, setOrderLineReceiptColumns] = useState<Array<IEditableDataGridColumnDef>>([]);
  const [precision, setPrecision] = useState<number>(0);

  const orderLineColumnMeta = useOrderLineColumnMeta();

  const [runGoodsReceiptHelperQuery, { data }] = useLazyQuery<
    IGoodsReceiptHelperQueryResult,
    IGoodsReceiptHelperQueryVariables
  >(GoodsReceiptHelperQuery);

  useEffect(() => {
    if (data?.data && data.data.lotAttributes) {
      const columns: Array<IEditableDataGridColumnDef> = [
        {
          name: 'User',
          key: 'createdBy',
          editable: false,
          resizable: true,
        },
        {
          name: 'Date',
          key: 'createdAt',
          editable: false,
          resizable: true,
          formatter: Formatters.DateTimeFormatter,
        },
        {
          name: 'Bin Location',
          key: 'binLocation.location',
          editable: false,
          resizable: true,
        },
      ];
      const { lotAttributes } = data.data;
      const attrs = [...lotAttributes] as Array<ILotAttributeConfig>; //Typecast as we know the contents of the array will exist from the query.
      if (attrs.length > 0) {
        attrs.sort((a, b) => a.sortOrder - b.sortOrder);
        attrs.forEach((attr, index) => {
          let column: Maybe<IEditableDataGridColumnDef> = null;
          switch (attr.attributeType) {
            case AttributeType.Text:
              column = {
                name: attr.title,
                key: `stockLot.lotAttributeValues[${index}].textValue`,
                resizable: true,
                editable: false,
              };
              break;
            case AttributeType.Date:
              column = {
                name: attr.title,
                key: `stockLot.lotAttributeValues[${index}].dateValue`,
                resizable: true,
                formatter: Formatters.DateFormatter,
                editable: false,
              };
              break;
            case AttributeType.Integer:
              column = {
                name: attr.title,
                key: `stockLot.lotAttributeValues[${index}].integerValue`,
                resizable: true,
                formatter: Formatters.NumericFormatter,
                options: {
                  format: { decimals: 0 },
                },
                cellClass: 'rightAlign',
                editable: false,
              };
              break;
            case AttributeType.Numeric:
              column = {
                name: attr.title,
                key: `stockLot.lotAttributeValues[${index}].doubleValue`,
                formatter: Formatters.NumericFormatter,
                options: {
                  format: { decimals: 2 },
                },
                resizable: true,
                cellClass: 'rightAlign',
                editable: false,
              };
              break;
            case AttributeType.Sequential:
              column = {
                name: attr.title,
                key: `stockLot.lotAttributeValues[${index}].integerValue`,
                resizable: true,
                editable: false,
              };
              break;
            default:
              break;
          }
          if (column) columns.push(column);
        });
      }

      columns.push({
        name: 'Received',
        key: 'received',
        formatter: Formatters.NumericFormatter,
        editable: false,
        resizable: true,
        cellClass: 'rightAlign',
        options: {
          format: { decimals: precision },
        },
      });

      columns.push({
        name: 'Allocated',
        key: 'stockLot.allocated',
        formatter: Formatters.NumericFormatter,
        editable: false,
        resizable: true,
        cellClass: 'rightAlign',
        options: {
          format: { decimals: precision },
        },
      });

      columns.push({
        name: 'Free',
        key: 'stockLot.freeStock',
        formatter: Formatters.NumericFormatter,
        editable: false,
        resizable: true,
        cellClass: 'rightAlign',
        options: {
          format: { decimals: precision },
        },
      });

      columns.push({
        name: 'Previously Cancelled',
        key: 'cancelled',
        formatter: Formatters.NumericFormatter,
        editable: false,
        resizable: true,
        cellClass: 'rightAlign',
        options: {
          format: { decimals: precision },
        },
      });

      columns.push({
        name: 'To Cancel',
        key: 'newCancellation',
        formatter: Formatters.NumericFormatter,
        editable: true,
        resizable: true,
        cellClass: 'rightAlign',
        options: {
          format: { decimals: precision },
        },
      });

      setOrderLineReceiptColumns(columns);
    }
  }, [data]);

  const withPurchaseOrderLineRowUpdated = (commit: ICommit<IPurchaseOrderLine>) => {
    if (commit.cellKey === 'product') {
      const id = commit.updated?.product?.id;
      if (id && id > 0) {
        setPrecision(getQuantityPrecision(commit.updated));
        setRowIdx(commit.rowIdx);
        getAttributeColumns(id);
      }
    }
  };

  const onCellSelected: IOnCellSelectedFunction<IPurchaseOrderLine> = (dataid, selected, rowData) => {
    if (rowData.product) {
      setPrecision(getQuantityPrecision(rowData));
      setRowIdx(selected.rowIdx);
      getAttributeColumns(rowData.product.id);
    }
  };

  const getAttributeColumns = (productId: number) => {
    runGoodsReceiptHelperQuery({ variables: { productId: productId, warehouseId: null } }).then(() => {});
  };

  const gridHeight = Math.floor((window.innerHeight - 320) / 2);

  const purchaseOrder = props.values.purchaseOrder;
  const orderNumber = purchaseOrder?.orderNumber;
  const supplierDescription = `${purchaseOrder?.supplier?.reference} ${purchaseOrder?.supplier?.name}`;

  return (
    <Form.FormContainer>
      <Form.Header
        entityHdr="orderReference"
        orderNumber={orderNumber}
        component={PurchaseOrderHeader}
        field="Receipt"
        saveOptions={PURCHASE_ORDER_CANCELLATION_SAVE_OPTIONS}
        showActions={false}
      />
      <Form.Form>
        <Form.Row>
          <Form.Fieldset cols={3 / 12}>
            <Form.Field<IDateFieldProps>
              dataid="purchaseOrder.orderDate"
              component={input.DateField}
              label="Order Date"
              labelCols={4 / 12}
              inputCols={8 / 12}
              readOnly={true}
            />
            <Form.Field<IDateFieldProps>
              dataid="purchaseOrder.targetDate"
              component={input.DateField}
              label="Expected Delivery Date"
              labelCols={4 / 12}
              inputCols={8 / 12}
              readOnly={true}
            />
          </Form.Fieldset>
          <Form.Fieldset cols={5 / 12}>
            <BlurTextField
              data-testid={'supplier-description'}
              value={supplierDescription}
              label="Supplier"
              autoFocus={true}
              readOnly={true}
            />
            <AddressInfo address={purchaseOrder?.supplier?.address} entityType={'supplier'} />
            <Form.Field
              dataid="purchaseOrder.orderReference"
              component={input.BlurTextField}
              label="Order Reference"
              readOnly={true}
            />
          </Form.Fieldset>
          <Form.Fieldset cols={2 / 12}>
            <OrderTotals
              field="purchaseOrder"
              archived={purchaseOrder?.archived}
              carriageCalculationMethod={purchaseOrder?.carriageCostCalculationMethod}
              originModule={OrderTotalsOriginModule.Purchasing}
            />
          </Form.Fieldset>
        </Form.Row>
        <Form.Row>
          <Form.Fieldset cols={5 / 12}>
            <Form.Field
              dataid="reference"
              component={input.BlurTextField}
              label="Stock Transaction Document Reference"
              hint={'As appears in the Stock Transaction workspace and the Transactions tab of the product form'}
            />
          </Form.Fieldset>
        </Form.Row>
        <Form.TabPanel>
          <Form.TabContent id="items" label="Receipt Lines">
            <Form.Row>
              <Form.Field<IEditableDataGridProps>
                dataid="purchaseOrder.orderLines"
                component={input.DataGrid}
                label=""
                columns={orderLineColumnMeta}
                enableCellSelect={true}
                enableCellAutoFocus={true}
                addBlankRow={false}
                withRowUpdated={withPurchaseOrderLineRowUpdated}
                onCellSelected={onCellSelected}
                contentHeight={gridHeight}
              />
            </Form.Row>
            {rowIdx >= 0 ? (
              <Form.Row>
                <Form.Field<IEditableDataGridProps>
                  dataid={`purchaseOrder.orderLines.${rowIdx}.receipts`}
                  component={input.DataGrid}
                  label=""
                  columns={orderLineReceiptColumns}
                  enableCellSelect={true}
                  enableCellAutoFocus={true}
                  addBlankRow={false}
                  contentHeight={gridHeight}
                />
              </Form.Row>
            ) : null}
          </Form.TabContent>
        </Form.TabPanel>
      </Form.Form>
    </Form.FormContainer>
  );
};

function submitValues(props: { values: IPurchaseOrderReceiptAdjustmentInput }) {
  const values = cloneDeep(props.values);

  const purchaseOrderReceiptAdjustment = {
    reference: values.reference,
    purchaseOrder: values.purchaseOrder,
  };

  const orderLines = values.purchaseOrder?.orderLines;
  if (orderLines && orderLines.length > 0) {
    (orderLines as Array<IPurchaseOrderLineInput>).forEach((line) => {
      if (!line.receipts || line.receipts.length === 0) return;
      line.receipts.forEach((receipt) => {
        delete receipt!.stockLot;
      });
    });
  }

  return purchaseOrderReceiptAdjustment;
}

const ConnectedPurchaseOrderCancelReceiptForm = createForm<ComponentProps<typeof PurchaseOrderCancelReceiptForm>>({
  form: type,
  name: formTitle,
  entity: PurchaseOrderCancelReceipt,
  defaultReturnUrl: PurchaseOrder.workspaceUrl,
  getSubmitValues: submitValues,
  ignoreUpdatePermission: true,
})(PurchaseOrderCancelReceiptForm);

export default (props: Omit<ComponentProps<typeof BankForm>, 'values' | 'change'>) => {
  const formDefault = useFormQuery({ instance });
  const [formUpdate] = useMutation(update!);

  return <ConnectedPurchaseOrderCancelReceiptForm {...props} formUpdate={formUpdate} formData={formDefault} />;
};
